import axios from 'axios';
import config from '../config.js';

// Export apiClient for use in loggingService
export { apiClient };

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: config.BACKEND_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and domain tracking
apiClient.interceptors.request.use(
  (config) => {
    // Optionally enrich request with domain information for logging on the backend
    // Extract domain from user email if available (for add-in context)
    if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox) {
      try {
        const userEmail = Office.context.mailbox.userProfile.emailAddress;
        if (userEmail) {
          const domain = userEmail.split('@')[1];
          if (domain) {
            // Add domain to query params for logging
            config.params = config.params || {};
            config.params.domain = domain.toLowerCase();
          }
        }
      } catch (e) {
        // Ignore errors in domain extraction
      }
    }
    
    return config;
  },
  (error) => {
    // console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    // console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  // Check user permission
  checkUserPermission: async (email) => {
    const response = await apiClient.get(config.ENDPOINTS.CHECK_PERMISSION, {
      params: { email }
    });
    return response.data;
  },

  // Sync user settings
  syncUserSettings: async (email, settings) => {
    const response = await apiClient.put(`/api/auth/syncSettings/${email}`, settings);
    return response.data;
  }
};

// Email Operations API
export const emailAPI = {
  // Forward email by add-in (phishing simulation check)
  forwardByAddIn: async (emailData, endpointUrl) => {
    const startTime = Date.now();
    let statusCode = null;
    let responseData = null;
    let errorMessage = null;
    
    try {
      const response = await axios.post(endpointUrl, emailData, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      statusCode = response.status;
      responseData = response.data;
      const duration = Date.now() - startTime;
      
      // Log the external API call
      const { logExternalCall } = await import('./loggingService.js');
      await logExternalCall({
        method: 'POST',
        url: endpointUrl,
        path: endpointUrl,
        statusCode: statusCode,
        statusMessage: response.statusText,
        requestBody: emailData,
        responseBody: responseData,
        duration: duration,
      });
      
      return responseData;
    } catch (error) {
      statusCode = error.response?.status || 500;
      errorMessage = error.message || 'Unknown error';
      const duration = Date.now() - startTime;
      
      // Log the failed external API call
      const { logExternalCall } = await import('./loggingService.js');
      await logExternalCall({
        method: 'POST',
        url: endpointUrl,
        path: endpointUrl,
        statusCode: statusCode,
        statusMessage: error.response?.statusText || 'Error',
        requestBody: emailData,
        errorMessage: errorMessage,
        duration: duration,
      });
      
      throw error;
    }
  }
};

// Template API
export const templateAPI = {
  // Get all templates (with user-specific customizations if userEmail provided)
  getTemplates: async (userEmail = null) => {
    const params = userEmail ? { userEmail } : {};
    const response = await apiClient.get('/api/templates', { params });
    // Backend returns { success: true, data: templates }
    // Return the data array directly
    return response.data?.data || response.data || [];
  },

  // Get single template by ID
  getTemplate: async (id) => {
    const response = await apiClient.get(`/api/templates/${id}`);
    return response.data;
  },

  // Update template (for super-admin in dashboard)
  updateTemplate: async (id, templateData) => {
    const response = await apiClient.put(`/api/templates/${id}`, templateData);
    return response.data;
  },

  // Save user-specific template customization (for add-in users)
  customizeTemplate: async (templateId, userEmail, customizationData) => {
    const response = await apiClient.post(`/api/templates/${templateId}/customize`, {
      userEmail,
      ...customizationData
    });
    return response.data;
  },
};

// Logs API
export const logsAPI = {
  // Get logs with optional filtering
  getLogs: async (params = {}) => {
    const response = await apiClient.get('/api/logs', { params });
    return response.data;
  },

  // Get log statistics
  getLogStats: async (params = {}) => {
    const response = await apiClient.get('/api/logs/stats', { params });
    return response.data;
  },

  // Get single log by ID
  getLog: async (id) => {
    const response = await apiClient.get(`/api/logs/${id}`);
    return response.data;
  },
};

// Utility functions
export const apiUtils = {
  // Format error message
  formatErrorMessage: (error) => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    return error.message || 'An unknown error occurred';
  },

  // Validate required fields
  validateRequiredFields: (data, requiredFields) => {
    const missingFields = requiredFields.filter(field => !data[field]);
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }
  }
};

export default {
  authAPI,
  emailAPI,
  templateAPI,
  apiUtils
};
